import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import { formatCLP, formatUSD, resolveImageUrl } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";

export default function Cart() {
    const { user } = useAuth();
    const { cart, removeItem, clear, refreshServerCart } = useCart();
    const navigate = useNavigate();

    const [providers, setProviders] = useState(null);
    const [couponInput, setCouponInput] = useState("");
    const [quote, setQuote] = useState(null);      // server-computed quote for logged-in user
    const [couponError, setCouponError] = useState("");
    const [validating, setValidating] = useState(false);
    const [loading, setLoading] = useState("");

    useEffect(() => {
        api.get("/checkout/providers").then(({ data }) => setProviders(data)).catch(() => setProviders({}));
    }, []);

    // Auto-refresh quote (no coupon) whenever the cart items change and user is logged in
    useEffect(() => {
        if (!user || cart.count === 0) { setQuote(null); return; }
        api.post("/cart/quote", { coupon_code: couponInput || null })
            .then(({ data }) => setQuote(data))
            .catch(() => setQuote(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, cart.count]);

    // Local (guest) totals fallback when there is no quote
    const localTotals = useMemo(() => {
        const sub_clp = cart.subtotal_clp || 0;
        const sub_usd = cart.subtotal_usd || 0;
        return {
            subtotal_clp: sub_clp,
            subtotal_usd: sub_usd,
            discount_clp: 0, discount_usd: 0, discount_pct: 0,
            total_clp: sub_clp, total_usd: sub_usd,
        };
    }, [cart]);

    const totals = quote || localTotals;

    const applyCoupon = async () => {
        const code = couponInput.trim().toUpperCase();
        if (!code) return;
        if (!user) { toast.info("Log in to apply a coupon"); return; }
        setValidating(true); setCouponError("");
        try {
            const { data } = await api.post("/cart/quote", { coupon_code: code });
            setQuote(data);
            if (data.coupon) {
                toast.success(`Coupon ${data.coupon.code}: ${data.discount_pct}% off applied to the whole cart`);
                setCouponError("");
            } else {
                setCouponError(mapCouponReason(data.coupon_reason));
            }
        } catch (e) {
            setCouponError(apiErrorMessage(e));
        } finally {
            setValidating(false);
        }
    };

    const removeCoupon = () => {
        setCouponInput("");
        setCouponError("");
        // Re-quote without coupon
        if (user) {
            api.post("/cart/quote", { coupon_code: null }).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
        }
    };

    const requireAuth = () => {
        if (!user) {
            toast.info("Log in to check out your cart");
            navigate("/login", { state: { from: "/cart" } });
            return false;
        }
        return true;
    };

    const payWithFlow = async () => {
        if (!requireAuth()) return;
        setLoading("flow");
        try {
            const { data } = await api.post("/cart/checkout/flow", { coupon_code: quote?.coupon?.code || null });
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            } else {
                toast.error("No payment URL received");
                setLoading("");
            }
        } catch (e) {
            toast.error(apiErrorMessage(e));
            setLoading("");
            await refreshServerCart();
        }
    };

    const payWithPayPal = async () => {
        if (!requireAuth()) return;
        setLoading("paypal");
        try {
            const { data } = await api.post("/cart/checkout/paypal", { coupon_code: quote?.coupon?.code || null });
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            } else {
                toast.error("No PayPal URL received");
                setLoading("");
            }
        } catch (e) {
            toast.error(apiErrorMessage(e));
            setLoading("");
            await refreshServerCart();
        }
    };

    const isEmpty = cart.count === 0;

    return (
        <div className="min-h-screen flex flex-col" data-testid="cart-page">
            <PublicHeader />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex-1 w-full">
                <div className="mb-10">
                    <div className="label-mono mb-3">/ Cart</div>
                    <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter">
                        Your <span className="text-avx-blue">cart</span>
                    </h1>
                    {!isEmpty && (
                        <p className="text-avx-muted mt-2">
                            {cart.count} {cart.count === 1 ? "item" : "items"} · one single payment with Flow or PayPal
                        </p>
                    )}
                </div>

                {isEmpty ? (
                    <div className="panel-elevated p-10 text-center max-w-xl mx-auto" data-testid="cart-empty">
                        <div className="font-heading font-bold text-2xl mb-3">Your cart is empty</div>
                        <p className="text-sm text-avx-muted mb-6 leading-relaxed">
                            Browse the catalog and add the plugins you want. You can pay for them all in a single transaction.
                        </p>
                        <Link to="/plugins" className="btn-primary inline-block px-6 py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="cart-empty-cta">
                            View catalog →
                        </Link>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-[1fr_360px] gap-10">
                        {/* ITEMS COLUMN */}
                        <div className="space-y-3" data-testid="cart-items">
                            {cart.items.map((it, i) => (
                                <div key={it.key} className="panel-elevated p-5 flex gap-5 items-start" data-testid={`cart-item-${it.key}`}>
                                    <Link
                                        to={it.kind === "bundle" ? `/bundles/${it.slug}` : `/plugins/${it.slug}`}
                                        className="shrink-0 w-24 h-24 rounded-sm border border-avx-line bg-avx-bg overflow-hidden"
                                    >
                                        {it.image_url ? (
                                            <img src={resolveImageUrl(it.image_url)} alt={it.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="font-heading font-black text-3xl text-avx-line">{it.name.charAt(0)}</span>
                                            </div>
                                        )}
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="label-mono mb-1">/ {it.kind === "bundle" ? "Bundle" : "Plugin"}</div>
                                                <Link
                                                    to={it.kind === "bundle" ? `/bundles/${it.slug}` : `/plugins/${it.slug}`}
                                                    className="font-heading font-bold text-lg leading-tight hover:text-avx-blue transition-colors block"
                                                >
                                                    {it.name}
                                                </Link>
                                            </div>
                                            <button
                                                onClick={() => removeItem(it.key)}
                                                className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-danger hover:bg-avx-danger/10 px-3 py-1.5 rounded-sm border border-avx-danger/30 shrink-0"
                                                data-testid={`cart-remove-${it.key}`}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <div className="mt-3 flex items-baseline gap-3">
                                            {it.sale_price > 0 ? (
                                                <>
                                                    <span className="font-mono text-avx-muted text-xs line-through">{formatCLP(it.base_price)}</span>
                                                    <span className="font-heading font-bold text-avx-danger text-lg">{formatCLP(it.effective_price)}</span>
                                                    <span className="text-[10px] font-mono text-avx-muted">
                                                        {formatUSD((cart.items_usd?.[i]?.effective_price) ?? 0)}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="font-heading font-bold text-avx-blue text-lg">{formatCLP(it.effective_price)}</span>
                                                    <span className="text-[10px] font-mono text-avx-muted">
                                                        {formatUSD((cart.items_usd?.[i]?.effective_price) ?? 0)}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="flex justify-between pt-2">
                                <Link to="/plugins" className="text-xs font-mono uppercase tracking-[0.2em] text-avx-blue hover:underline" data-testid="cart-continue-shopping">
                                    ← Keep browsing plugins
                                </Link>
                                <button
                                    onClick={() => { if (window.confirm("Empty the entire cart?")) clear(); }}
                                    className="text-xs font-mono uppercase tracking-[0.2em] text-avx-muted hover:text-avx-danger"
                                    data-testid="cart-clear-all"
                                >
                                    Empty cart
                                </button>
                            </div>
                        </div>

                        {/* SUMMARY COLUMN */}
                        <aside className="space-y-4 lg:sticky lg:top-24 self-start" data-testid="cart-summary">
                            <div className="panel-elevated p-6">
                                <div className="label-mono mb-4">/ Overview</div>
                                <dl className="text-sm space-y-3">
                                    <div className="flex justify-between">
                                        <dt className="text-avx-muted">Subtotal</dt>
                                        <dd className="font-mono">
                                            <div>{formatCLP(totals.subtotal_clp)}</div>
                                            <div className="text-[10px] text-avx-muted">{formatUSD(totals.subtotal_usd)}</div>
                                        </dd>
                                    </div>
                                    {totals.discount_pct > 0 && (
                                        <div className="flex justify-between text-avx-success" data-testid="cart-discount-row">
                                            <dt>Coupon −{totals.discount_pct}%</dt>
                                            <dd className="font-mono">
                                                <div>−{formatCLP(totals.discount_clp)}</div>
                                                <div className="text-[10px]">−{formatUSD(totals.discount_usd)}</div>
                                            </dd>
                                        </div>
                                    )}
                                    <div className="flex justify-between pt-3 border-t border-avx-line" data-testid="cart-total-row">
                                        <dt className="font-heading font-bold text-base">Total</dt>
                                        <dd className="font-heading font-bold text-avx-blue text-lg text-right">
                                            <div>{formatCLP(totals.total_clp)}</div>
                                            <div className="text-[10px] font-mono text-avx-muted">{formatUSD(totals.total_usd)}</div>
                                        </dd>
                                    </div>
                                </dl>

                                {/* Coupon */}
                                {user && (
                                    quote?.coupon ? (
                                        <div className="mt-5 panel p-3 bg-avx-success/8 border-avx-success/40 flex items-center justify-between gap-3" data-testid="cart-coupon-applied">
                                            <div>
                                                <div className="font-heading font-bold text-sm flex items-center gap-2">
                                                    <span className="font-mono text-avx-success">{quote.coupon.code}</span>
                                                    <span className="text-avx-success">−{quote.discount_pct}%</span>
                                                </div>
                                                <div className="text-[10px] font-mono text-avx-muted mt-1">Applied to the cart subtotal</div>
                                            </div>
                                            <button onClick={removeCoupon} className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline" data-testid="cart-coupon-remove">
                                                Remove
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="mt-5" data-testid="cart-coupon-block">
                                            <div className="label-mono mb-2">/ Have a coupon?</div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={couponInput}
                                                    onChange={(e) => setCouponInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]+/g, ""))}
                                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                                                    placeholder="BLACKFRIDAY"
                                                    disabled={validating}
                                                    className="flex-1 bg-avx-bg border border-avx-line h-10 px-3 rounded-sm font-mono text-xs uppercase focus:outline-none focus:border-avx-blue"
                                                    data-testid="cart-coupon-input"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={applyCoupon}
                                                    disabled={!couponInput || validating}
                                                    className="btn-secondary px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm disabled:opacity-50"
                                                    data-testid="cart-coupon-apply"
                                                >
                                                    {validating ? "..." : "Apply"}
                                                </button>
                                            </div>
                                            {couponError && (
                                                <div className="text-[10px] font-mono text-avx-danger mt-2" data-testid="cart-coupon-error">{couponError}</div>
                                            )}
                                        </div>
                                    )
                                )}
                                {!user && (
                                    <div className="mt-4 text-[10px] font-mono text-avx-muted leading-relaxed">
                                        <Link to="/login" state={{ from: "/cart" }} className="text-avx-blue hover:underline">Log in</Link> to use coupons and pay for your cart.
                                    </div>
                                )}
                            </div>

                            {/* Pay buttons */}
                            <div className="panel-elevated p-6 space-y-3">
                                <div className="label-mono mb-2">/ Pay everything with</div>
                                <button
                                    onClick={payWithFlow}
                                    disabled={loading === "flow" || (providers && !providers.flow?.enabled) || totals.total_clp < 350}
                                    className="w-full panel p-4 hover:border-avx-blue transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                                    data-testid="cart-pay-flow"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <div className="font-heading font-bold text-sm">Flow <span className="label-mono text-avx-success ml-2">Chile · CLP</span></div>
                                            <div className="text-xs text-avx-muted mt-1">Webpay, OnePay, bank transfer, MercadoPago</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-heading font-bold text-avx-blue">{formatCLP(totals.total_clp)}</div>
                                            <div className="text-[10px] font-mono text-avx-muted group-hover:text-avx-blue">
                                                {loading === "flow" ? "Redirecting…" : "Pay →"}
                                            </div>
                                        </div>
                                    </div>
                                    {providers && !providers.flow?.enabled && (
                                        <div className="text-[10px] font-mono text-avx-danger mt-2">Pending configuration</div>
                                    )}
                                </button>

                                <button
                                    onClick={payWithPayPal}
                                    disabled={loading === "paypal" || (providers && !providers.paypal?.enabled) || totals.total_usd <= 0}
                                    className="w-full panel p-4 hover:border-avx-blue transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                                    data-testid="cart-pay-paypal"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <div className="font-heading font-bold text-sm">PayPal <span className="label-mono text-avx-info ml-2">International · USD</span></div>
                                            <div className="text-xs text-avx-muted mt-1">Cards + PayPal accounts</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-heading font-bold text-avx-blue">{formatUSD(totals.total_usd)}</div>
                                            <div className="text-[10px] font-mono text-avx-muted group-hover:text-avx-blue">
                                                {loading === "paypal" ? "Redirecting…" : "Pay →"}
                                            </div>
                                        </div>
                                    </div>
                                    {providers && !providers.paypal?.enabled && (
                                        <div className="text-[10px] font-mono text-avx-danger mt-2">Pending configuration</div>
                                    )}
                                </button>

                                <div className="pt-3 mt-2 border-t border-avx-line text-[10px] font-mono text-avx-muted leading-relaxed">
                                    Payments processed by Flow.cl and PayPal. Plugins are activated automatically in your dashboard once payment is confirmed.
                                </div>
                            </div>
                        </aside>
                    </div>
                )}
            </section>

            <PublicFooter />
        </div>
    );
}


function mapCouponReason(reason) {
    switch (reason) {
        case "not_found": return "Coupon not found.";
        case "disabled": return "Coupon disabled.";
        case "expired": return "Coupon expired.";
        case "max_uses_reached": return "Coupon usage limit reached.";
        case "not_applicable": return "This coupon does not apply to the items in your cart.";
        case "already_used": return "You already used this coupon before.";
        default: return "Invalid coupon.";
    }
}
